/**
 * AUTO-GENERATED by tools/gen-circle-lut.ts — DO NOT EDIT BY HAND.
 *
 * Precomputed unit-circle offsets for deterministic circular motion (Brief 012). The
 * engine cannot call Math.sin/cos (ESLint determinism guard), so circular hazards —
 * the fire bar and the track/spinning saw — index INTO this baked table instead. The
 * values are literal doubles → bit-identical every load → replay-deterministic.
 *
 * dy is +down (screen space). CIRCLE_LUT[i] = [cos(theta), sin(theta)] for
 * theta = i / CIRCLE_STEPS * 2*PI. Index 0 = [1, 0] (pointing right).
 */
export const CIRCLE_STEPS = 64;

export const CIRCLE_LUT: readonly (readonly [number, number])[] = [
  [1, 0],
  [0.995185, 0.098017],
  [0.980785, 0.19509],
  [0.95694, 0.290285],
  [0.92388, 0.382683],
  [0.881921, 0.471397],
  [0.83147, 0.55557],
  [0.77301, 0.634393],
  [0.707107, 0.707107],
  [0.634393, 0.77301],
  [0.55557, 0.83147],
  [0.471397, 0.881921],
  [0.382683, 0.92388],
  [0.290285, 0.95694],
  [0.19509, 0.980785],
  [0.098017, 0.995185],
  [0, 1],
  [-0.098017, 0.995185],
  [-0.19509, 0.980785],
  [-0.290285, 0.95694],
  [-0.382683, 0.92388],
  [-0.471397, 0.881921],
  [-0.55557, 0.83147],
  [-0.634393, 0.77301],
  [-0.707107, 0.707107],
  [-0.77301, 0.634393],
  [-0.83147, 0.55557],
  [-0.881921, 0.471397],
  [-0.92388, 0.382683],
  [-0.95694, 0.290285],
  [-0.980785, 0.19509],
  [-0.995185, 0.098017],
  [-1, 0],
  [-0.995185, -0.098017],
  [-0.980785, -0.19509],
  [-0.95694, -0.290285],
  [-0.92388, -0.382683],
  [-0.881921, -0.471397],
  [-0.83147, -0.55557],
  [-0.77301, -0.634393],
  [-0.707107, -0.707107],
  [-0.634393, -0.77301],
  [-0.55557, -0.83147],
  [-0.471397, -0.881921],
  [-0.382683, -0.92388],
  [-0.290285, -0.95694],
  [-0.19509, -0.980785],
  [-0.098017, -0.995185],
  [0, -1],
  [0.098017, -0.995185],
  [0.19509, -0.980785],
  [0.290285, -0.95694],
  [0.382683, -0.92388],
  [0.471397, -0.881921],
  [0.55557, -0.83147],
  [0.634393, -0.77301],
  [0.707107, -0.707107],
  [0.77301, -0.634393],
  [0.83147, -0.55557],
  [0.881921, -0.471397],
  [0.92388, -0.382683],
  [0.95694, -0.290285],
  [0.980785, -0.19509],
  [0.995185, -0.098017],
];

/** Wrap any (possibly negative) index into [0, CIRCLE_STEPS). */
function wrap(index: number): number {
  return ((index % CIRCLE_STEPS) + CIRCLE_STEPS) % CIRCLE_STEPS;
}

/**
 * The LUT index for a circular mover after `step` fixed steps, completing one full
 * revolution every `stepsPerRev` steps in direction `dir` (+1 clockwise, -1 ccw).
 * Pure integer/IEEE math (Math.floor + multiply/divide/mod — none are banned), so a
 * replay reproduces it exactly. After exactly `stepsPerRev` steps the index returns to
 * its start (step*CIRCLE_STEPS is divisible by stepsPerRev) — the determinism contract.
 */
export function circleIndex(step: number, stepsPerRev: number, dir: 1 | -1): number {
  const raw = Math.floor((step * CIRCLE_STEPS) / stepsPerRev);
  return wrap(dir === 1 ? raw : -raw);
}

/** The [dx, dy] unit offset at a LUT index (wrapped). Multiply by a radius for px. */
export function circleOffset(index: number): readonly [number, number] {
  return CIRCLE_LUT[wrap(index)];
}
